<template>
  <template v-if="isZoomPhoneRecordingVisible">
    <BLayout class="zoom-phone-recording">
      <BTooltip
        v-if="isAudioVisible"
        bottom
        :content="tooltipZoomPhoneRecording"
        :disabled="!tooltipZoomPhoneRecording"
      >
        <div
          class="audio-wrap"
          :class="{ 'disabled': shouldShowUnplayableAudio }"
        >
          <BPlaceholder
            v-loading="isAudioProcessing"
            height="0"
            width="0"
            element-loading-background="transparent"
          />
          <audio
            :style="styles"
            controls
            :src="zoomPhoneRecordingUrl"
          />
        </div>
      </BTooltip>
      <div
        v-if="isTranscriptionLinkVisible"
        class="transcription-wrap"
      >
        <RouterLink
          :to="transcriptionRoute"
          class="transcription-link"
        >
          <BIcon
            class="icon"
            hover
            outlined
            size="large"
          >
            speech_to_text
          </BIcon>
        </RouterLink>
      </div>
    </BLayout>
    <BDivider class="my-600" />
  </template>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { FileStorageAccessApiService } from '@/api/user/resources/file_storage_access';
import { useAvailableFeatureCheck } from '@/composable/available-feature-check';
import { useCurrentUserProvider } from '@/composable/current-user';

export default defineComponent({
  props: {
    zoomPhoneRecording: Object,
    transcription: Object,
    callResult: Object,
    width: Number,
    isZoomPhoneConnected: Boolean,
  },
  setup() {
    const { inject: injectCurrentUser } = useCurrentUserProvider();
    const { authorize } = injectCurrentUser();
    const { isAvailableTranscriptionFeature } = useAvailableFeatureCheck();
    return {
      authorize,
      isAvailableTranscriptionFeature,
    };
  },
  data() {
    return {
      zoomPhoneRecordingUrl: '',
    };
  },
  computed: {
    tooltipZoomPhoneRecording() {
      if (this.isAudioProcessing) return this.$t('zoomPhoneRecording.inProgress');
      if (this.zoomPhoneRecording?.expired) return this.$t('zoomPhoneRecording.expired');
      if (this.isAudioFailed) return this.$t('zoomPhoneRecording.failed');
      return '';
    },
    styles() {
      return {
        'width': this.width,
      };
    },
    isAudioProcessing() {
      return this.zoomPhoneRecording?.processingStatus === 'processing' || this.zoomPhoneRecording?.processingStatus === 'not_started' && this.isZoomPhoneConnected;
    },
    isAudioFailed() {
      return this.zoomPhoneRecording?.processingStatus === 'failed';
    },
    shouldShowUnplayableAudio() {
      return this.isAudioProcessing
        || this.isAudioFailed
        || !!this.zoomPhoneRecording?.expired;
    },
    isAudioVisible() {
      return this.zoomPhoneRecording?.id != null || !!this.shouldShowUnplayableAudio;
    },
    isTranscriptionLinkVisible() {
      if (!this.isAvailableTranscriptionFeature) return false;
      if (this.transcription?.id != null) return true;
      // 文字起こしデータがない場合は「音声があるはず」の条件で判定する
      return this.isZoomPhoneConnected;
    },
    isZoomPhoneRecordingVisible() {
      if (this.authorize(['bpo_guest'])) return false;
      if (this.callResult.zoomPhoneCallId == null) return false;
      if (!this.callResult.connected) return false;
      return this.isAudioVisible;
    },
    transcriptionRoute() {
      return {
        name: 'Transcription',
        params: {
          callResultId: this.callResult.id,
        },
      };
    },
  },
  created() {
    this.setZoomPhoneRecordingUrlIfNeeded();
  },
  methods: {
    async setZoomPhoneRecordingUrlIfNeeded(): Promise<void> {
      const zoomPhoneRecordingId: number = this.zoomPhoneRecording?.id;
      if (!zoomPhoneRecordingId) return;
      if (this.shouldShowUnplayableAudio) return;
      const apiService = new FileStorageAccessApiService();
      const { data } = await apiService.createZoomPhoneRecordingPresignedUrl({
        request: {
          createZoomPhoneRecordingPresignedUrlRequest: {
            zoomPhoneRecordingId,
          },
        },
      });
      this.zoomPhoneRecordingUrl = data.url;
    },
  },
});
</script>

<style lang="scss" scoped>
audio {
  width: 500px;
}

.audio-wrap {
  display: flex;
  align-items: center;
  margin-right: $basespace-300;

  &.disabled {
    --el-loading-spinner-size: 24px;

    .placeholder {
      left: 14px;
    }

    audio::-webkit-media-controls-play-button {
      -webkit-appearance: none;
    }
  }
}

.transcription-wrap {
  display: flex;
  align-items: center;

  .transcription-link {
    text-decoration: none;
    display: inline-flex;

    &.disabled {
      pointer-events: none;
      opacity: 0.5;
    }
  }

  .icon {
    cursor: pointer;
  }
}
</style>
